/**
 * NEXUS UI - HoloCard
 * Metric display container with edge glow and hover effects
 */

import React from 'react';
import type { BaseProps, NexusVariant } from './types';

export interface HoloCardProps extends BaseProps {
  children: React.ReactNode;
  label?: string;
  icon?: React.ReactNode;
  variant?: NexusVariant;
  hoverable?: boolean;
  onClick?: () => void;
}

const variantStyles: Record<NexusVariant, { accent: string; glow: string }> = {
  cyan: { accent: '#00d4ff', glow: 'rgba(0, 212, 255, 0.15)' },
  blue: { accent: '#3b82f6', glow: 'rgba(59, 130, 246, 0.15)' },
  violet: { accent: '#a855f7', glow: 'rgba(168, 85, 247, 0.15)' },
  green: { accent: '#00ff88', glow: 'rgba(0, 255, 136, 0.15)' },
  amber: { accent: '#ffaa00', glow: 'rgba(255, 170, 0, 0.15)' },
  red: { accent: '#ff3366', glow: 'rgba(255, 51, 102, 0.15)' },
};

export const HoloCard: React.FC<HoloCardProps> = ({
  children,
  label,
  icon,
  variant = 'cyan',
  hoverable = true,
  onClick,
  className = '',
  style,
}) => {
  const colors = variantStyles[variant];

  return (
    <div
      className={`nx-holo-card ${hoverable ? 'nx-holo-card--hoverable' : ''} ${className}`}
      style={{
        '--card-accent': colors.accent,
        '--card-glow': colors.glow,
        cursor: onClick ? 'pointer' : undefined,
        ...style,
      } as React.CSSProperties}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {(label || icon) && (
        <div className="nx-holo-card__header">
          {label && <span className="nx-holo-card__label">{label}</span>}
          {icon && <span className="nx-holo-card__icon">{icon}</span>}
        </div>
      )}
      <div className="nx-holo-card__body">{children}</div>
    </div>
  );
};

export default HoloCard;
